import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../services/contentApi';

const WebsiteDataContext = createContext();

export const useWebsiteData = () => {
  const context = useContext(WebsiteDataContext);
  if (!context) {
    throw new Error('useWebsiteData must be used within a WebsiteDataProvider');
  }
  return context;
};

export const WebsiteDataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data States
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [calculators, setCalculators] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [aboutSettings, setAboutSettings] = useState(null);

  // Initial Data Fetch
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [
          fetchedServices,
          fetchedTestimonials,
          fetchedCalculators,
          fetchedTeam,
          fetchedBlogs,
          fetchedProjects,
          fetchedSettings,
          fetchedAboutSettings
        ] = await Promise.all([
          api.getServices(),
          api.getTestimonials(),
          api.listCalculatorConfigs(),
          api.getTeamMembers(),
          api.getBlogs(),
          api.getProjects(),
          api.getAppSettings(),
          api.getAboutSettings()
        ]);

        setServices(fetchedServices);
        setTestimonials(fetchedTestimonials);
        setCalculators(fetchedCalculators);
        setTeamMembers(fetchedTeam);
        setBlogs(fetchedBlogs);
        setProjects(fetchedProjects);
        setSettings(fetchedSettings || defaultSettings); // Fallback to defaults if no settings in DB
        setAboutSettings(fetchedAboutSettings || defaultAboutSettings);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // --- Admin Operations Wrappers ---

  // Projects
  const addProject = async (data) => {
    try {
      const newProject = await api.saveProject(null, { ...data, status: data.status || 'completed' });
      setProjects(prev => [newProject, ...prev]);
      return newProject;
    } catch (err) {
      console.error("Error adding project:", err);
      throw err;
    }
  };

  const updateProject = async (id, data) => {
    try {
      const updated = await api.saveProject(id, data);
      setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updated } : p));
      return updated;
    } catch (err) {
      console.error("Error updating project:", err);
      throw err;
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Error deleting project:", err);
      throw err;
    }
  };

  const toggleProjectStatus = async (id) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;

    let newStatus = 'completed';
    if (project.status === 'completed' || project.status === 'in-progress') newStatus = 'hidden';
    else if (project.status === 'hidden' || project.status === 'draft') newStatus = 'completed';

    await updateProject(id, { status: newStatus });
  };

  // Blogs
  const addBlog = async (data) => {
    try {
      const newBlog = await api.saveBlog(null, { ...data, status: data.status || 'draft' });
      setBlogs(prev => [newBlog, ...prev]);
    } catch (err) { console.error(err); throw err; }
  };

  const updateBlog = async (id, data) => {
    try {
      const updated = await api.saveBlog(id, data);
      setBlogs(prev => prev.map(b => b.id === id ? { ...b, ...updated } : b));
    } catch (err) { console.error(err); throw err; }
  };

  const deleteBlog = async (id) => {
    try {
      await api.deleteBlog(id);
      setBlogs(prev => prev.filter(b => b.id !== id));
    } catch (err) { console.error(err); throw err; }
  };

  const toggleBlogStatus = async (id) => {
    const blog = blogs.find(b => b.id === id);
    if (!blog) return;
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    await updateBlog(id, { status: newStatus });
  };

  // Services
  const addService = async (data) => {
    const res = await api.saveService(null, { ...data, status: 'active' });
    setServices(prev => [...prev, res]);
  };
  const updateService = async (id, data) => {
    const res = await api.saveService(id, data);
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...res } : s));
  };
  const deleteService = async (id) => {
    await api.deleteService(id);
    setServices(prev => prev.filter(s => s.id !== id));
  };
  const toggleServiceStatus = async (id) => {
    const s = services.find(x => x.id === id);
    if (s) await updateService(id, { status: s.status === 'active' ? 'inactive' : 'active' });
  };

  // Testimonials
  const addTestimonial = async (data) => {
    const res = await api.saveTestimonial(null, { ...data, status: 'pending' });
    setTestimonials(prev => [...prev, res]);
  };
  const updateTestimonial = async (id, data) => {
    const res = await api.saveTestimonial(id, data);
    setTestimonials(prev => prev.map(t => t.id === id ? { ...t, ...res } : t));
  };
  const deleteTestimonial = async (id) => {
    await api.deleteTestimonial(id);
    setTestimonials(prev => prev.filter(t => t.id !== id));
  };
  const toggleTestimonialStatus = async (id) => {
    const t = testimonials.find(x => x.id === id);
    if (t) await updateTestimonial(id, { status: t.status === 'published' ? 'pending' : 'published' });
  };

  // Team
  const addTeamMember = async (data) => {
    const res = await api.saveTeamMember(null, { ...data, status: 'active' });
    setTeamMembers(prev => [...prev, res]);
  };
  const updateTeamMember = async (id, data) => {
    const res = await api.saveTeamMember(id, data);
    setTeamMembers(prev => prev.map(m => m.id === id ? { ...m, ...res } : m));
  };
  const deleteTeamMember = async (id) => {
    await api.deleteTeamMember(id);
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  };

  // Calculators
  const addCalculator = async (data) => {
    const res = await api.saveCalculatorConfig(null, { ...data, status: 'active', usage: 0 });
    setCalculators(prev => [...prev, res]);
  };
  const updateCalculator = async (id, data) => {
    const res = await api.saveCalculatorConfig(id, data);
    setCalculators(prev => prev.map(c => c.id === id ? { ...c, ...res } : c));
  };
  const deleteCalculator = async (id) => {
    console.warn('Delete calculator restricted: Permission denied.');
  };
  const toggleCalculatorStatus = async (id) => {
    const c = calculators.find(x => x.id === id);
    if (c) await updateCalculator(id, { status: c.status === 'active' ? 'inactive' : 'active' });
  };
  const incrementCalculatorUsage = async (id) => {
    const c = calculators.find(x => x.id === id);
    if (c) {
      await updateCalculator(id, { usage: (c.usage || 0) + 1 });
    }
  };

  // Settings
  const updateSettings = async (newSettings) => {
    const merged = { ...settings, ...newSettings };
    await api.saveAppSettings(merged);
    setSettings(merged);
  };

  const updateAboutSettings = async (newAboutSettings) => {
    const merged = { ...aboutSettings, ...newAboutSettings };
    await api.saveAboutSettings(merged);
    setAboutSettings(merged);
  };

  // Public Getters (Filtering)
  const getActiveServices = () => services.filter(s => s.status === 'active');
  const getPublishedTestimonials = () => testimonials.filter(t => t.status === 'published');
  const getActiveCalculators = () => calculators.filter(c => c.status === 'active');
  const getActiveTeamMembers = () => teamMembers.filter(m => m.status === 'active');
  const getPublishedBlogs = () => blogs.filter(b => b.status === 'published');
  const getActiveProjects = () => projects.filter(p => p.status === 'completed' || p.status === 'in-progress');
  const getAllProjects = () => projects;

  const value = {
    loading,
    error,

    services,
    testimonials,
    calculators,
    teamMembers,
    blogs,
    projects,
    settings,
    aboutSettings,

    addService, updateService, deleteService, toggleServiceStatus,
    addTestimonial, updateTestimonial, deleteTestimonial, toggleTestimonialStatus,
    addCalculator, updateCalculator, deleteCalculator, toggleCalculatorStatus, incrementCalculatorUsage,
    addTeamMember, updateTeamMember, deleteTeamMember,
    addBlog, updateBlog, deleteBlog, toggleBlogStatus,
    addProject, updateProject, deleteProject, toggleProjectStatus,
    updateSettings,
    updateAboutSettings,

    getActiveServices,
    getPublishedTestimonials,
    getActiveCalculators,
    getActiveTeamMembers,
    getPublishedBlogs,
    getActiveProjects,
    getAllProjects
  };

  return (
    <WebsiteDataContext.Provider value={value}>
      {children}
    </WebsiteDataContext.Provider>
  );
};

const defaultAboutSettings = {
  aboutIntro: "Behind this platform is Nirmitee Associates — a design studio driven by creativity, technical expertise, and seamless project coordination. With over 5 years of professional experience in architecture, planning, execution, and liaisoning, the studio was founded by Ar. Mrunal Bhilare and Mr. Akash Patil with a shared vision of delivering thoughtfully planned and efficiently executed projects.",
  quoteText: "Together, the founders bring a balanced combination of creative architectural design and efficient project coordination, enabling Nirmitee Associates to deliver projects seamlessly from concept to completion while creating functional, innovative, and practical solutions tailored to client requirements.",
  quoteAuthor: "Nirmitee Associates Co-Founders Vision"
};

const defaultSettings = {
  general: {
    siteName: 'Nirmitee Associates',
    siteDescription: 'Leading provider of construction, regulatory, and consulting services in Pune',
    contactEmail: 'nirmitee.associates2919@gmail.com',
    contactPhone: '+91 80039 89393',
    address: 'Pune, Maharashtra, India',
    workingHours: 'Mon-Fri: 9:00 AM - 6:00 PM'
  },
  social: {
    facebook: 'https://facebook.com/nirmitee',
    instagram: 'https://instagram.com/nirmitee'
  },
  navigation: {
    showServices: true,
    showCalculators: true,
    showBlog: true,
    showTeam: true,
    showTestimonials: true,
    showAbout: true,
    showContact: true
  },
  appearance: {
    theme: 'light'
  },
  seo: {
    metaTitle: 'Nirmitee Associates',
    metaDescription: 'Construction & Regulatory Services'
  }
};